import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { filter, flatMap, isArray, map, size, sortBy, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import {
  AutomaticConditionAttributesTable,
  CellFiltersTypes,
  ConditionType,
  FilterActions,
} from 'interfaces/automaticCondition';
import { NumberOption, OrchestrationDataType } from 'interfaces/calculateFeatures';
import { CategoricalOption, MappingFilterMetadata } from 'interfaces/postProcessingAction';
import { uuidv4 } from 'utils/helpers';
import { CellPopulationGroupState } from './cellPopulationGroupRestore';

interface SelectOption {
  id: string;
  label: string;
  type?: OrchestrationDataType;
  options?: CategoricalOption[] | NumberOption;
}

const isPositivityAttributeId = (attributeId: string) => attributeId.endsWith('.pos');
const isBinnedAttributeId = (attributeId: string) => attributeId.endsWith('.binned');
const isSpotCountAttributeId = (attributeId: string) =>
  attributeId.includes('counts_in_segmentation') &&
  !isPositivityAttributeId(attributeId) &&
  !isBinnedAttributeId(attributeId);

const isPositivityConditionAttribute = (attribute: SelectOption) =>
  attribute.type === OrchestrationDataType.Boolean ||
  isPositivityAttributeId(attribute.id) ||
  isSpotCountAttributeId(attribute.id);

const PopulationMultiSelect: React.FC<{
  label: string;
  options: SelectOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
}> = ({ label, options, selectedIds, onChange }) => {
  const optionIds = map(options, 'id');
  const optionById = useMemo(() => Object.fromEntries(map(options, (option) => [option.id, option])), [options]);
  const allSelected = size(selectedIds) === size(optionIds) && size(optionIds) > 0;
  const selectedOptions = map(selectedIds, (id) => optionById[id]).filter(Boolean);

  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedOptions}
        disabled={!size(options)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_event, selected) => onChange(map(selected, 'id'))}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              size(options) ? `Search ${label.toLocaleLowerCase()}` : `No ${label.toLocaleLowerCase()} available`
            }
            size="small"
          />
        )}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button size="small" disabled={!size(options) || allSelected} onClick={() => onChange(optionIds)}>
          Select all
        </Button>
        <Button size="small" disabled={!size(selectedIds)} onClick={() => onChange([])}>
          Clear
        </Button>
      </Stack>
    </Box>
  );
};

export const getCellPopulationOptions = (mappingFiltersMetadata: MappingFilterMetadata[]) => {
  const nonAreaMetadata = filter(mappingFiltersMetadata, (metadata) => metadata.sourceName !== 'area');
  const labelMetadata = filter(
    flatMap(nonAreaMetadata, (metadata) =>
      map(metadata.data, (attribute) => ({ sourceName: metadata.sourceName, attribute }))
    ),
    ({ attribute }) => attribute.label === 'label' && attribute.type === OrchestrationDataType.Categorical
  );
  const cellClassOptions = sortBy(
    uniqBy(
      flatMap(labelMetadata, ({ attribute }) =>
        isArray(attribute.options)
          ? map(attribute.options as CategoricalOption[], ({ id, label }) => ({ id: `${id}`, label: `${label}` }))
          : []
      ),
      'id'
    ),
    'label'
  );
  const allCellAttributeOptions = sortBy(
    flatMap(nonAreaMetadata, (metadata) =>
      map(
        filter(metadata.data, (attribute) => attribute.label !== 'label'),
        (attribute) => ({
          id: `${metadata.sourceName}.${attribute.label}`,
          label: attribute.displayLabel || attribute.label,
          type: attribute.type,
          options: attribute.options,
        })
      )
    ),
    'label'
  );
  const positivityAttributeIds = new Set(
    map(
      filter(allCellAttributeOptions, (attribute) => isPositivityAttributeId(attribute.id)),
      (attribute) => attribute.id.replace(/\.pos$/, '')
    )
  );
  const cellAttributeOptions = filter(
    allCellAttributeOptions,
    (attribute) => !(isSpotCountAttributeId(attribute.id) && positivityAttributeIds.has(attribute.id))
  );

  return { cellClassOptions, cellAttributeOptions };
};

const getDefaultAttributeCondition = (attribute: SelectOption): AutomaticConditionAttributesTable => {
  if (isPositivityConditionAttribute(attribute)) {
    return {
      conditionType: ConditionType.ATTRIBUTES_TABLE,
      mappingFilter: attribute.id,
      filterAction: isSpotCountAttributeId(attribute.id) ? CellFiltersTypes.filter_gte : CellFiltersTypes.filter_eq,
      toFilter: 1,
      displayValue: attribute.label,
    };
  }

  return {
    conditionType: ConditionType.ATTRIBUTES_TABLE,
    mappingFilter: attribute.id,
    filterAction:
      attribute.type === OrchestrationDataType.Categorical ? CellFiltersTypes.filter_in : CellFiltersTypes.filter_gte,
    toFilter: attribute.type === OrchestrationDataType.Categorical ? [] : undefined,
    displayValue: attribute.label,
  };
};

const CellAttributeCondition: React.FC<{
  attribute: SelectOption;
  condition: AutomaticConditionAttributesTable;
  index: number;
  onChange: (condition: AutomaticConditionAttributesTable) => void;
  onRemove: () => void;
}> = ({ attribute, condition, index, onChange, onRemove }) => {
  const isPositivityCondition = isPositivityConditionAttribute(attribute);
  const isNegativeCountCondition =
    isSpotCountAttributeId(attribute.id) &&
    condition.filterAction === CellFiltersTypes.filter_eq &&
    Number(condition.toFilter) === 0;
  const positivityValue = isNegativeCountCondition ? '0' : `${condition.toFilter ?? 1}`;
  const isCategorical = attribute.type === OrchestrationDataType.Categorical;
  const filterActions = attribute.type ? FilterActions[attribute.type] || [] : [];
  const categoricalOptions = isArray(attribute.options) ? (attribute.options as CategoricalOption[]) : [];
  const numberRange = !isArray(attribute.options) ? (attribute.options as NumberOption | undefined) : undefined;

  return (
    <Grid container spacing={1} alignItems="center" sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
      <Grid item xs={12} md={1}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {index === 0 ? 'WHERE' : 'AND'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <Typography variant="body2" fontWeight={600}>
          {attribute.label}
        </Typography>
      </Grid>
      {isPositivityCondition ? (
        <Grid item xs={10} md={7}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              IS
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={positivityValue}
              onChange={(_event, value) => {
                if (value === null) {
                  return;
                }
                if (isSpotCountAttributeId(attribute.id)) {
                  onChange({
                    ...condition,
                    filterAction: value === '0' ? CellFiltersTypes.filter_eq : CellFiltersTypes.filter_gte,
                    toFilter: value === '0' ? 0 : 1,
                  });
                  return;
                }
                onChange({ ...condition, filterAction: CellFiltersTypes.filter_eq, toFilter: Number(value) });
              }}
              aria-label={`${attribute.label} condition`}
            >
              <ToggleButton value="1">Positive</ToggleButton>
              <ToggleButton value="0">Negative</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Operator</InputLabel>
              <Select
                label="Operator"
                value={condition.filterAction}
                onChange={(event) => onChange({ ...condition, filterAction: event.target.value })}
              >
                {map(filterActions, (filterAction) => (
                  <MenuItem key={filterAction.id} value={filterAction.id}>
                    {filterAction.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={10} md={4}>
            {isCategorical ? (
              <PopulationMultiSelect
                label="Values"
                options={map(categoricalOptions, ({ id, label }) => ({
                  id: `${id}`,
                  label: `${label}`,
                  type: OrchestrationDataType.Categorical,
                }))}
                selectedIds={map(condition.toFilter as string[] | number[], (value) => `${value}`)}
                onChange={(values) => onChange({ ...condition, toFilter: values })}
              />
            ) : (
              <TextField
                fullWidth
                label="Value"
                size="small"
                type="number"
                value={condition.toFilter ?? ''}
                inputProps={{ min: numberRange?.min, max: numberRange?.max }}
                onChange={(event) => onChange({ ...condition, toFilter: Number(event.target.value) })}
                helperText={
                  numberRange?.min !== undefined || numberRange?.max !== undefined
                    ? `Available range: ${numberRange?.min ?? 'any'} to ${numberRange?.max ?? 'any'}`
                    : 'Set the threshold used to include cells.'
                }
              />
            )}
          </Grid>
        </>
      )}
      <Grid item xs={2} md={1} sx={{ textAlign: 'right' }}>
        <IconButton aria-label={`Remove ${attribute.label} condition`} onClick={onRemove}>
          <DeleteOutlineIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export const CellPopulationGroups: React.FC<{
  mappingFiltersMetadata: MappingFilterMetadata[];
  groups?: CellPopulationGroupState[];
  initialGroups?: CellPopulationGroupState[];
  onGroupsChange?: (groups: CellPopulationGroupState[]) => void;
}> = ({ mappingFiltersMetadata, groups: controlledGroups, initialGroups, onGroupsChange }) => {
  const [localGroups, setLocalGroups] = useState<CellPopulationGroupState[]>(initialGroups || []);
  const groups = controlledGroups ?? localGroups;
  const setGroups = (update: React.SetStateAction<CellPopulationGroupState[]>) => {
    const updatedGroups = typeof update === 'function' ? update(groups) : update;
    if (controlledGroups === undefined) {
      setLocalGroups(updatedGroups);
    }
    onGroupsChange?.(updatedGroups);
  };
  const { cellClassOptions, cellAttributeOptions } = useMemo(
    () => getCellPopulationOptions(mappingFiltersMetadata),
    [mappingFiltersMetadata]
  );

  const updateGroup = (groupId: string, updates: Partial<CellPopulationGroupState>) =>
    setGroups((currentGroups) =>
      map(currentGroups, (group) => (group.id === groupId ? { ...group, ...updates } : group))
    );

  const cellAttributeOptionsById = useMemo(
    () => Object.fromEntries(map(cellAttributeOptions, (attribute) => [attribute.id, attribute])),
    [cellAttributeOptions]
  );

  const updateSelectedAttributes = (group: CellPopulationGroupState, cellAttributeIds: string[]) => {
    const existingConditionsByAttributeId = Object.fromEntries(
      map(group.attributeConditions, (condition) => [
        (condition as AutomaticConditionAttributesTable).mappingFilter,
        condition,
      ])
    );
    updateGroup(group.id, {
      cellAttributeIds,
      attributeConditions: map(cellAttributeIds, (attributeId) => {
        const existingCondition = existingConditionsByAttributeId[attributeId] as AutomaticConditionAttributesTable;
        return existingCondition || getDefaultAttributeCondition(cellAttributeOptionsById[attributeId]);
      }),
    });
  };

  useEffect(() => {
    if (controlledGroups === undefined && initialGroups) {
      setLocalGroups(initialGroups);
    }
  }, [initialGroups, controlledGroups]);

  useEffect(() => {
    setGroups((currentGroups) => {
      let didHydrateConditions = false;
      const hydratedGroups = map(currentGroups, (group) => {
        const conditionByAttributeId = Object.fromEntries(
          map(group.attributeConditions, (condition) => [
            (condition as AutomaticConditionAttributesTable).mappingFilter,
            condition,
          ])
        );
        const attributeConditions = map(group.cellAttributeIds, (attributeId) => {
          const existingCondition = conditionByAttributeId[attributeId] as AutomaticConditionAttributesTable;
          if (existingCondition) {
            return existingCondition;
          }
          didHydrateConditions = true;
          return getDefaultAttributeCondition(cellAttributeOptionsById[attributeId]);
        });

        return didHydrateConditions ? { ...group, attributeConditions } : group;
      });

      return didHydrateConditions ? hydratedGroups : currentGroups;
    });
  }, [cellAttributeOptionsById]);

  return (
    <Box sx={{ width: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Include cells from any selected class that also match every condition in the group.
      </Typography>
      <Button
        startIcon={<AddIcon />}
        onClick={() =>
          setGroups((currentGroups) => [
            ...currentGroups,
            {
              id: uuidv4(),
              name: `Cell Population ${size(currentGroups) + 1}`,
              cellClassIds: [],
              cellAttributeIds: [],
              attributeConditions: [],
            },
          ])
        }
      >
        Add Cell Population
      </Button>
      {!size(cellClassOptions) && !size(cellAttributeOptions) && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No cell classes or cell attributes are available for this stain.
        </Alert>
      )}
      {map(groups, (group, index) => (
        <Paper key={group.id} variant="outlined" sx={{ mt: 1, p: 1.5, borderRadius: 1 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} container alignItems="center" justifyContent="space-between">
              <Grid item xs zeroMinWidth>
                <TextField
                  fullWidth
                  label={`Population ${index + 1} name`}
                  size="small"
                  value={group.name || `Cell Population ${index + 1}`}
                  onChange={(event) => updateGroup(group.id, { name: event.target.value })}
                />
              </Grid>
              <IconButton
                aria-label={`Remove Cell Population ${index + 1}`}
                onClick={() =>
                  setGroups((currentGroups) => filter(currentGroups, (currentGroup) => currentGroup.id !== group.id))
                }
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Cell Classes
              </Typography>
              <PopulationMultiSelect
                label="Included classes"
                options={cellClassOptions}
                selectedIds={group.cellClassIds}
                onChange={(cellClassIds) => updateGroup(group.id, { cellClassIds })}
              />
              <FormHelperText>A cell may belong to any selected class.</FormHelperText>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Conditions{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  (all must match)
                </Typography>
              </Typography>
              <Autocomplete
                options={filter(cellAttributeOptions, (option) => !group.cellAttributeIds.includes(option.id))}
                value={null}
                disabled={!size(cellAttributeOptions)}
                getOptionLabel={(option) => option.label}
                onChange={(_event, attribute) => {
                  if (attribute) {
                    updateSelectedAttributes(group, [...group.cellAttributeIds, attribute.id]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add condition"
                    placeholder={size(cellAttributeOptions) ? 'Search cell attributes' : 'No cell attributes available'}
                    size="small"
                  />
                )}
              />
              {size(group.attributeConditions) === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No conditions added. This population currently includes the selected classes without attribute
                  filtering.
                </Typography>
              ) : (
                map(group.cellAttributeIds, (attributeId, conditionIndex) => {
                  const attribute = cellAttributeOptionsById[attributeId];
                  const condition = group.attributeConditions[conditionIndex] as AutomaticConditionAttributesTable;
                  return attribute && condition ? (
                    <CellAttributeCondition
                      key={attributeId}
                      attribute={attribute}
                      condition={condition}
                      index={conditionIndex}
                      onChange={(updatedCondition) => {
                        const attributeConditions = [...group.attributeConditions];
                        attributeConditions[conditionIndex] = updatedCondition;
                        updateGroup(group.id, { attributeConditions });
                      }}
                      onRemove={() =>
                        updateSelectedAttributes(
                          group,
                          filter(group.cellAttributeIds, (currentAttributeId) => currentAttributeId !== attributeId)
                        )
                      }
                    />
                  ) : null;
                })
              )}
            </Grid>
          </Grid>
        </Paper>
      ))}
    </Box>
  );
};
